import { Box, Button, Container, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { motion } from "framer-motion";
import heroImg from "../../static/Home/Hero.webp";

function HeroSection() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const DARK_GREEN = "#052e16";
  const GREEN = "#064e2a";
  const MID_GREEN = "#15803d";
  const LIGHT_GREEN = "#e8f5ec";
  const SOFT_GREEN = "#f4fbf6";

  return (
    <Box
      sx={{
        width: "100%",
        background:
          "linear-gradient(180deg, #ffffff 0%, #f4fbf6 55%, #e8f5ec 100%)",
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          py: { xs: 2.5, md: 4.5 },
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: "100%",
            display: "flex",
            justifyContent: "center",
            borderRadius: { xs: "18px", md: "28px" },
            overflow: "visible",
            px: { xs: 0, md: 2 },
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: { xs: "100%", sm: "96%", md: "92%" },
              borderRadius: { xs: "18px", md: "28px" },
              p: { xs: 1, md: 1.5 },
              background:
                "linear-gradient(135deg, rgba(6,78,42,.20), rgba(232,245,236,.95))",
              boxShadow: {
                xs: "0 16px 34px rgba(6,78,42,.18)",
                md: "0 26px 64px rgba(6,78,42,.24)",
              },
              border: "1px solid rgba(6,78,42,.14)",
              "&::before": {
                content: '""',
                position: "absolute",
                inset: { xs: 8, md: 12 },
                borderRadius: { xs: "16px", md: "24px" },
                background:
                  "linear-gradient(180deg, rgba(5,46,22,.04), rgba(5,46,22,.28))",
                zIndex: 1,
                pointerEvents: "none",
              },
              "&::after": {
                content: '""',
                position: "absolute",
                width: { xs: 120, md: 220 },
                height: { xs: 120, md: 220 },
                borderRadius: "50%",
                right: { xs: -30, md: -60 },
                top: { xs: -30, md: -70 },
                background:
                  "radial-gradient(circle, rgba(21,128,61,.20), transparent 68%)",
                zIndex: 0,
                pointerEvents: "none",
              },
            }}
          >
            <Box
              component="img"
              src={heroImg}
              alt="Torneo de Fútbol"
              sx={{
                position: "relative",
                zIndex: 1,
                width: "100%",
                height: "auto",
                borderRadius: { xs: "14px", md: "22px" },
                display: "block",
                objectFit: "cover",
                filter: "contrast(1.04) saturate(1.06)",
              }}
            />

            {isDesktop && (
              <Box
                sx={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  bottom: "6%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 2,
                  zIndex: 3,
                }}
              >
                <Box
                  component={motion.div}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, ease: "easeOut" }}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Button
                    href="/calendario-de-partidos"
                    disableElevation
                    sx={{
                      px: 5,
                      height: 54,
                      borderRadius: "14px",
                      backgroundColor: GREEN,
                      color: "#fff",
                      fontWeight: 900,
                      textTransform: "none",
                      boxShadow: "0 12px 28px rgba(6,78,42,.32)",
                      border: "1px solid rgba(255,255,255,.18)",
                      "&:hover": {
                        backgroundColor: DARK_GREEN,
                        boxShadow: "0 16px 36px rgba(6,78,42,.42)",
                      },
                    }}
                  >
                    Ver calendario
                  </Button>
                </Box>

                <Box
                  component={motion.div}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, ease: "easeOut", delay: 0.08 }}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Button
                    href="/galeria"
                    disableElevation
                    sx={{
                      px: 5,
                      height: 54,
                      borderRadius: "14px",
                      backgroundColor: "#fff",
                      color: GREEN,
                      border: "2px solid",
                      borderColor: GREEN,
                      fontWeight: 900,
                      textTransform: "none",
                      boxShadow: "0 12px 28px rgba(6,78,42,.20)",
                      "&:hover": {
                        backgroundColor: LIGHT_GREEN,
                        borderColor: DARK_GREEN,
                        color: DARK_GREEN,
                        boxShadow: "0 16px 34px rgba(6,78,42,.28)",
                      },
                    }}
                  >
                    Ver galería
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </Box>

        {!isDesktop && (
          <Box
            sx={{
              display: "flex",
              gap: 2,
              mt: 3,
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <Box
              component={motion.div}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              whileHover={{ scale: 1.08 }}
              whileTap={{ scale: 0.98 }}
            >
              <Button
                href="/calendario-de-partidos"
                disableElevation
                sx={{
                  px: 3,
                  height: 44,
                  borderRadius: "14px",
                  backgroundColor: GREEN,
                  color: "#fff",
                  fontWeight: 900,
                  textTransform: "none",
                  boxShadow: "0 10px 22px rgba(6,78,42,.28)",
                  "&:hover": {
                    backgroundColor: DARK_GREEN,
                  },
                }}
              >
                Ver calendario
              </Button>
            </Box>

            <Box
              component={motion.div}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: "easeOut", delay: 0.08 }}
              whileHover={{ scale: 1.08 }}
              whileTap={{ scale: 0.98 }}
            >
              <Button
                href="/galeria"
                disableElevation
                sx={{
                  px: 3,
                  height: 44,
                  borderRadius: "14px",
                  backgroundColor: "#fff",
                  color: GREEN,
                  border: "2px solid",
                  borderColor: GREEN,
                  fontWeight: 900,
                  textTransform: "none",
                  boxShadow: "0 10px 22px rgba(6,78,42,.14)",
                  "&:hover": {
                    backgroundColor: SOFT_GREEN,
                    borderColor: DARK_GREEN,
                    color: DARK_GREEN,
                  },
                }}
              >
                Ver galería
              </Button>
            </Box>
          </Box>
        )}
      </Container>
    </Box>
  );
}

export default HeroSection;